// Reference rates shown on the landing page; the simulation is illustrative.
export const FINANCING_ANNUAL_RATE = 0.1119;
export const CONSORTIUM_MONTHLY_ADMIN_FEE = 0.00073;
export const HOME_EQUITY_MONTHLY_RATE = 0.0165;
const INCOME_COMMITMENT = 0.3;
const CONSORTIUM_REFERENCE_BID = 0.3;

export interface FinancingInput {
	propertyValue: number;
	downPaymentPercent: number;
	years: number;
}

export interface FinancingResult {
	financedAmount: number;
	downPayment: number;
	sacFirstInstallment: number;
	sacLastInstallment: number;
	priceInstallment: number;
	suggestedIncome: number;
}

export interface ConsortiumInput {
	letterValue: number;
	months: number;
}

export interface ConsortiumResult {
	installment: number;
	adminFeePercent: number;
	referenceBid: number;
}

export interface HomeEquityInput {
	propertyValue: number;
	creditPercent: number;
	months: number;
}

export interface HomeEquityResult {
	credit: number;
	installment: number;
}

function getPriceInstallment(
	principal: number,
	monthlyRate: number,
	months: number
): number {
	return (principal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));
}

export function calculateFinancing(input: FinancingInput): FinancingResult {
	const monthlyRate = Math.pow(1 + FINANCING_ANNUAL_RATE, 1 / 12) - 1;
	const financedAmount =
		input.propertyValue * (1 - input.downPaymentPercent / 100);
	const months = Math.max(1, input.years) * 12;
	const amortization = financedAmount / months;
	const sacFirstInstallment = amortization + financedAmount * monthlyRate;

	return {
		financedAmount,
		downPayment: (input.propertyValue * input.downPaymentPercent) / 100,
		sacFirstInstallment,
		sacLastInstallment: amortization + amortization * monthlyRate,
		priceInstallment: getPriceInstallment(financedAmount, monthlyRate, months),
		suggestedIncome: sacFirstInstallment / INCOME_COMMITMENT
	};
}

export function calculateConsortium(input: ConsortiumInput): ConsortiumResult {
	const months = Math.max(1, input.months);
	const adminFee = CONSORTIUM_MONTHLY_ADMIN_FEE * months;

	return {
		installment: (input.letterValue * (1 + adminFee)) / months,
		adminFeePercent: adminFee * 100,
		referenceBid: input.letterValue * CONSORTIUM_REFERENCE_BID
	};
}

export function calculateHomeEquity(input: HomeEquityInput): HomeEquityResult {
	const credit = (input.propertyValue * input.creditPercent) / 100;

	return {
		credit,
		installment: getPriceInstallment(
			credit,
			HOME_EQUITY_MONTHLY_RATE,
			Math.max(1, input.months)
		)
	};
}
